'use client';
import { motion } from 'framer-motion';
import { memo, useState, useMemo } from 'react';
// Vote type definition for stateless mode
type Vote = {
  messageId: string;
  type: 'up' | 'down';
};
import { IAIcon } from './icons';
import { Response } from './elements/response';
import { MessageContent } from './elements/message';
import { MessageActions } from './message-actions';
import { PreviewAttachment } from './preview-attachment';
import equal from 'fast-deep-equal';
import { cn, sanitizeText } from '@/lib/utils';
import { MessageEditor } from './message-editor';
import { MessageReasoning } from './message-reasoning';
import { ExpertModeButton } from './expert-mode-button';
import { parseExpertModePrompt, removeExpertModeTag } from '@/lib/utils/expert-mode-parser';
import { Loader } from './elements/loader';
import type { UseChatHelpers } from '@ai-sdk/react';
import type { ChatMessage } from '@/lib/types';
import type { PromptType } from '@/app/(chat)/api/chat/route';

const PurePreviewMessage = ({
  chatId,
  message,
  vote,
  isLoading,
  setMessages,
  regenerate,
  isReadonly,
  requiresScrollPadding,
  userId,
  companyName,
  userName,
  promptMode,
  basePath,
}: {
  chatId: string;
  message: ChatMessage;
  vote: Vote | undefined;
  isLoading: boolean;
  setMessages: UseChatHelpers<ChatMessage>['setMessages'];
  regenerate: UseChatHelpers<ChatMessage>['regenerate'];
  isReadonly: boolean;
  requiresScrollPadding: boolean;
  userId?: string | null;
  companyName?: string | null;
  userName?: string | null;
  promptMode?: PromptType;
  basePath?: string;
}) => {
  const [mode, setMode] = useState<'view' | 'edit'>('view');

  // Parse expert mode prompt from assistant messages
  const expertModePrompt = useMemo(() => {
    if (message.role !== 'assistant' || isLoading) return null;

    const textPart = message.parts.find(p => p.type === 'text');
    if (!textPart?.text) return null;

    return parseExpertModePrompt(textPart.text);
  }, [message, isLoading]);

  const attachmentsFromMessage = message.parts.filter(
    (part) => part.type === 'file',
  );

  return (
    <motion.div
      data-testid={`message-${message.role}`}
      className="group/message w-full"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      data-role={message.role}
    >
      <div
        className={cn('flex w-full items-start gap-2 md:gap-3', {
          'justify-end': message.role === 'user' && mode !== 'edit',
          'justify-start': message.role === 'assistant',
        })}
      >
        {message.role === 'assistant' && (
          <div className='-mt-1 flex size-8 shrink-0 items-center justify-center rounded-full border-1 bg-background' style={{ borderColor: '#000054' }}>
            <IAIcon size={14} />
          </div>
        )}

        <div
          className={cn('flex flex-col', {
            'gap-2 md:gap-4': message.parts?.some(
              (p) => p.type === 'text' && p.text?.trim(),
            ),
            'min-h-96': message.role === 'assistant' && requiresScrollPadding,
            'w-full':
              (message.role === 'assistant' &&
                message.parts?.some(
                  (p) => p.type === 'text' && p.text?.trim(),
                )) ||
              mode === 'edit',
            'max-w-[calc(100%-2.5rem)] sm:max-w-[min(fit-content,80%)]':
              message.role === 'user' && mode !== 'edit',
          })}
        >
          {attachmentsFromMessage.length > 0 && (
            <div
              data-testid={`message-attachments`}
              className="flex flex-row justify-end gap-2"
            >
              {attachmentsFromMessage.map((attachment) => (
                <PreviewAttachment
                  key={attachment.url}
                  attachment={{
                    name: attachment.filename ?? 'file',
                    contentType: attachment.mediaType,
                    url: attachment.url,
                  }}
                />
              ))}
            </div>
          )}

          {message.parts?.map((part, index) => {
            const { type } = part;
            const key = `message-${message.id}-part-${index}`;

            if (type === 'reasoning' && part.text?.trim().length > 0) {
              return (
                <MessageReasoning
                  key={key}
                  isLoading={isLoading}
                  reasoning={part.text}
                />
              );
            }

            if (type === 'text') {
              if (mode === 'view') {
                // Remove expert mode tag from display
                const displayText = expertModePrompt
                  ? removeExpertModeTag(part.text)
                  : part.text;

                return (
                  <div key={key}>
                    <MessageContent
                      data-testid="message-content"
                      className={cn({
                        'w-fit break-words rounded-lg px-3 py-2 text-right text-gray-700':
                          message.role === 'user',
                        'bg-transparent px-0 py-0 text-left text-gray-700':
                          message.role === 'assistant',
                      })}
                      style={
                        message.role === 'user'
                          ? { backgroundColor: '#e9ecef', border: '1px solid #dee2e6' }
                          : undefined
                      }
                    >
                      <Response>{sanitizeText(displayText)}</Response>
                    </MessageContent>
                  </div>
                );
              }

              if (mode === 'edit') {
                return (
                  <div
                    key={key}
                    className="flex w-full flex-row items-start gap-3"
                  >
                    <div className="size-8" />
                    <div className="min-w-0 flex-1">
                      <MessageEditor
                        key={message.id}
                        message={message}
                        setMode={setMode}
                        setMessages={setMessages}
                        regenerate={regenerate}
                      />
                    </div>
                  </div>
                );
              }
            }

            return null;
          })}

          {/* Show expert mode button if prompt detected and in simple mode */}
          {expertModePrompt && message.role === 'assistant' && !isLoading && promptMode === 'simple' && (
            <ExpertModeButton
              prompt={expertModePrompt}
              chatId={chatId}
              userId={userId}
              companyName={companyName}
              userName={userName}
              basePath={basePath}
            />
          )}

          {!isReadonly && (
            <MessageActions
              key={`action-${message.id}`}
              chatId={chatId}
              message={message}
              vote={vote}
              isLoading={isLoading}
              setMode={setMode}
            />
          )}
        </div>
      </div>
    </motion.div>
  );
};

export const PreviewMessage = memo(
  PurePreviewMessage,
  (prevProps, nextProps) => {
    if (prevProps.isLoading !== nextProps.isLoading) return false;
    if (prevProps.message.id !== nextProps.message.id) return false;
    if (prevProps.requiresScrollPadding !== nextProps.requiresScrollPadding)
      return false;
    if (!equal(prevProps.message.parts, nextProps.message.parts)) return false;
    if (!equal(prevProps.vote, nextProps.vote)) return false;

    return false;
  },
);

export const ThinkingMessage = () => {
  const role = 'assistant';

  return (
    <motion.div
      data-testid="message-assistant-loading"
      className="group/message w-full"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      data-role={role}
    >
      <div className="flex items-start justify-start gap-3">
        <div className='-mt-1 flex size-8 shrink-0 items-center justify-center rounded-full border-2 bg-background' style={{ borderColor: '#000054' }}>
          <IAIcon size={14} />
        </div>

        <div className="flex w-full flex-col gap-2 md:gap-4">
          <div className='flex items-center gap-2 p-0 text-gray-600 text-sm'>
            <Loader size={14} className="text-blue-500" />
            <LoadingText>Réflexion en cours...</LoadingText>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

const LoadingText = ({ children }: { children: React.ReactNode }) => {
  return (
    <motion.div
      animate={{ backgroundPosition: ['100% 50%', '-100% 50%'] }}
      transition={{
        duration: 1.5,
        repeat: Number.POSITIVE_INFINITY,
        ease: 'linear',
      }}
      style={{
        background:
          'linear-gradient(90deg, #3b82f6 0%, #3b82f6 35%, #1d4ed8 50%, #3b82f6 65%, #3b82f6 100%)',
        backgroundSize: '200% 100%',
        WebkitBackgroundClip: 'text',
        backgroundClip: 'text',
      }}
      className="flex items-center text-transparent"
    >
      {children}
    </motion.div>
  );
};
